import { fromPairs, toPairs } from "lodash"
import React, { useCallback, useState, useRef, useMemo } from "react"
import useOnClickOutside from "use-onclickoutside"

interface Option {
  name: string
  url: string
  dates: string
  selected: boolean
  cancelled: boolean | null
}

interface DropdownOptionProps {
  index: number;
  option: Option;
  selectOption: (o: Option) => void;
}

const DropdownOption = ({ index, option, selectOption }: DropdownOptionProps) => {
  const className = useMemo(() => [
    'raceweekSelector__options__item',
    option.selected && 'raceweekSelector__options__item--selected',
    option.cancelled && 'raceweekSelector__options__item--cancelled',
  ].filter((c) => !!c).join(" "), [option])
  
  return (
    <div
      key={index}
      className={className}
      onClick={() => selectOption(option)}
    >
      <span>{option.name}</span>
      <span>{option.dates}</span>
    </div>
  )
}

interface OpenedDropdownProps {
  options: Option[];
  selectOption: (o: Option) => void;
}

const OpenedDropdown: React.FC<OpenedDropdownProps> = ({options, selectOption}) => {
  return <div className="raceweekSelector__options">
    <div className="raceweekSelector__options__inner">
      {options.map((option, index) => {
        return <DropdownOption option={option} index={index} selectOption={selectOption} />
      })}
    </div>
  </div>
}

interface RaceweekSelectorProps {
  options: Option[]
}

const RaceweekSelector: React.FC<RaceweekSelectorProps> = ({ options }) => {
  const [open, setOpen] = useState(false)

  const ref = useRef<HTMLElement>()
  useOnClickOutside(ref as any, () => { if (open) setOpen(false) })

  const selectedIndex = useMemo(() => {
    return options.findIndex((option) => option.selected) 
  }, [options])

  const [selected, setSelected] = useState(
    selectedIndex >= 0 ? options[selectedIndex] : options.find((option) => option.selected)
  )

  const classNames = useMemo(() => {
      const prevClasses = [
        "raceweekSelector__arrowButton",
        "raceweekSelector__arrowButton--left",
        selectedIndex < 1 && "raceweekSelector__arrowButton--inactive",
      ]
      const dropdownClasses = [
        'raceweekSelector__button',
        open && 'raceweekSelector__button--open',
      ]
      const nextClasses = [
        "raceweekSelector__arrowButton",
        "raceweekSelector__arrowButton--right",
        selectedIndex >= (options.length - 1) && "raceweekSelector__arrowButton--inactive",
      ]

      const obj = {
        prevClasses,
        dropdownClasses,
        nextClasses,
      } as const
      
      return fromPairs(
        toPairs(obj).map(([key, ary]) => {
          return [key, ary.filter((c) => !!c).join(" ")]
        })
      ) as Record<keyof typeof obj, string>
    },
    [open, selectedIndex],
  );


  const selectOption = (option: Option) => {
    setOpen(false)
    setSelected(option)
    window.location.href = option.url
  }

  const toPrevOption = useCallback(() => {
    const prevOption = selectedIndex >= 0 ? options[selectedIndex - 1] : undefined
    
    if (prevOption) {
      window.location.href = prevOption.url
    }
  }, [options, selectedIndex])

  const toNextOption = useCallback(() => {
    const nextOption = selectedIndex >= 0 ? options[selectedIndex + 1] : undefined

    if (nextOption) {
      window.location.href = nextOption.url
    }
  }, [options, selectedIndex])

  const toggleDropdown = useCallback(() => {
    setOpen((prev) => !prev);
  }, [])

  return <div ref={ref as any} className="raceweekSelector">
    <button className={classNames.prevClasses} onClick={toPrevOption} />
    <div className="raceweekSelector__button__container">
      <button
        className={classNames.dropdownClasses}
        aria-expanded={open}
        onClick={toggleDropdown}
      >
        <div className="raceweekSelector__button__text">
          <span>{selected?.name}</span>
          <span>{selected?.dates}</span>
        </div>
        <div className="raceweekSelector__button__arrow" />
      </button>
      {open && <OpenedDropdown options={options} selectOption={selectOption} />}
    </div>
    <button className={classNames.nextClasses} onClick={toNextOption} />
  </div>
}

export default RaceweekSelector
